// Official program shell: government header (seal, English + Urdu titles, live clock with Hijri date),
// gold notice bar, navigation and footer. Used by every public page; the staff and kiosk surfaces use
// the same header component in compact form.

import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';

export const PROGRAM = {
  en: 'Department of Health',
  sub: 'Government of Azad Jammu & Kashmir',
  ur: 'محکمہ صحت، حکومت آزاد جموں و کشمیر',
  program: 'AJK Smart Health Kiosk Program',
  programUr: 'آزاد کشمیر سمارٹ ہیلتھ کیوسک پروگرام',
};

const dateFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Karachi' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Asia/Karachi' });
const hijriFmt = (() => {
  try {
    return new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Karachi' });
  } catch {
    return null;
  }
})();

export function useClock(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function Clock({ compact = false }: { compact?: boolean }) {
  const now = useClock();
  return (
    <div className={`gov-clock ${compact ? 'compact' : ''}`} aria-label="Pakistan Standard Time">
      <div className="gov-time">{timeFmt.format(now)}</div>
      {!compact && <div className="gov-date">{dateFmt.format(now)}</div>}
      {!compact && hijriFmt && <div className="gov-hijri">{hijriFmt.format(now).replace(' AH', '')} AH</div>}
    </div>
  );
}

export function GovHeader({ title = PROGRAM.en, subtitle = PROGRAM.sub, urdu = PROGRAM.ur, compact = false, right, homeLink = true }: {
  title?: string; subtitle?: string; urdu?: string; compact?: boolean; right?: ReactNode;
  /** Off on kiosk surfaces, which must not navigate away. */
  homeLink?: boolean;
}) {
  const id = (
    <>
      <img src="/brand/seal.svg" alt="" className="gov-seal" />
      <div>
        <div className="gov-title">{title}</div>
        <div className="gov-subtitle">{subtitle}</div>
      </div>
    </>
  );
  return (
    <header className={`gov-header ${compact ? 'compact' : ''}`}>
      {homeLink ? <Link to="/" className="gov-id" aria-label="Home">{id}</Link> : <div className="gov-id">{id}</div>}
      <div className="gov-urdu" lang="ur" dir="rtl">{urdu}</div>
      <div className="gov-right">{right ?? <Clock compact={compact} />}</div>
    </header>
  );
}

export function NoticeBar({ items, badge = 'NOTICE', aside }: { items: string[]; badge?: string; aside?: ReactNode }) {
  return (
    <div className="gov-notice" role="note">
      <span className="gov-notice-badge">{badge}</span>
      <div className="gov-notice-track">
        <div className="gov-notice-items">{items.map((t) => <span key={t}>{t}</span>)}</div>
      </div>
      {aside && <span className="gov-notice-aside">{aside}</span>}
    </div>
  );
}

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/scope', label: 'Program scope' },
  { to: '/ministry', label: 'Ministry & governance' },
  { to: '/proposal', label: 'Proposal' },
];

const DEMO = [
  { to: '/kiosk', label: 'Health kiosk' },
  { to: '/clinician', label: 'Telemedicine' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/display', label: 'Waiting-room display' },
  { to: '/brief', label: 'Minister’s brief' },
];

export function SiteLayout({ children, notice }: { children: ReactNode; notice?: string[] }) {
  return (
    <div className="site">
      <GovHeader />
      <NoticeBar
        items={notice ?? [
          'AJK Smart Health Kiosk Program — proposal to the Department of Health, Government of AJK',
          '25-kiosk, 90-day pilot · scale pathway to 100 kiosks across all ten districts',
          'Live demonstration uses synthetic data only',
        ]}
        aside="Ref. MOVO-X/AJK/PROP/2026-001"
      />
      <nav className="gov-nav" aria-label="Main">
        <div className="gov-nav-inner">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `gov-nav-link ${isActive ? 'active' : ''}`}>{n.label}</NavLink>)}
          <span className="gov-nav-sep" />
          <span className="gov-nav-label">Live demo</span>
          {DEMO.map((n) => <Link key={n.to} to={n.to} className="gov-nav-link demo">{n.label}</Link>)}
        </div>
      </nav>
      <main className="site-main">{children}</main>
      <SiteFooter />
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="gov-footer">
      <div className="gov-footer-inner">
        <div className="gov-footer-col">
          <img src="/brand/seal.svg" alt="" width={44} height={44} />
          <div>
            <strong>{PROGRAM.program}</strong>
            <div lang="ur" dir="rtl" className="gov-footer-ur">{PROGRAM.programUr}</div>
          </div>
        </div>
        <div className="gov-footer-col small">
          <div><strong>Prepared for</strong> the Department of Health, Government of Azad Jammu &amp; Kashmir, Civil Secretariat, Muzaffarabad.</div>
          <div><strong>Prepared by</strong> MOVO-X Inc. · MOVO Global Enterprises (SMC-Private) Limited, Rawalpindi · info@movo-x.com</div>
        </div>
        <div className="gov-footer-col small">
          <div>Proposal demonstration — not an official Government of AJK website. Program seal shown for proposal purposes; the Department’s official emblem is applied on authorisation.</div>
          <div className="gov-powered">POWERED BY <span>MOVO-X</span></div>
        </div>
      </div>
    </footer>
  );
}

export function SectionTitle({ children, kicker }: { children: ReactNode; kicker?: string }) {
  return (
    <div className="gov-section-title">
      {kicker && <div className="gov-kicker">{kicker}</div>}
      <h2>{children}</h2>
      <div className="gov-rule" />
    </div>
  );
}
